/* eslint-disable max-lines */
import * as React from "react";
import * as moment from "moment";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";
import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { connect } from "react-redux";
import { Container, Loader, Button } from "semantic-ui-react";
import { uniq, flatten, values } from "lodash";

import { AccordionQuestionnaireContainer } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireContainer";
import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { openSection, updateQuestion, submitInvalidQuestionnaire } from "@teamprevent/common/questionnaires/actions";
import { AnsweredQuestion, AnsweredQuestionWithId, QuestionnaireTypes } from "@teamprevent/common/questionnaires/model";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { CompletedDate } from "@teamprevent/common/components/CompletedDate";
import { Appointment } from "@teamprevent/common/calendar";

import { ContextAwareCheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/ContextAwareCheckboxQuestion";

import { ApplicationState } from "../../applicationState";
import { CurrentUser } from "../../auth";
import { PartialOhCaseDetailModel, OhCaseDetailModel, OhCaseListModel } from "../../crud/ohCase";
import { getUserDisplayName } from "../../crud/adminUser/model";
import {
    loadQuestionnaire,
    submitDidNotAttend
} from "../actions";
import { routeIdSelector as ohCaseIdSelector } from "../../crud/ohCase/selectors";
import { EmployeeRecord, EmployeeRecordState } from "../../crud/employeeRecord";
import { loadEmployeeRecordDetail } from "../../crud/employeeRecord/actions";
import { ActionableTasksState } from "../../actionableTasks";
import { AdminUserDetailModel, loadAdminUsers } from "../../crud/adminUser";
import { adminUsersSelector } from "../../crud/adminUser/selectors";
import { TasksApi } from "../../tasks/tasksApi";
import { calendarApi } from "../../calendar/calendarApi";

import { DataPrivacySection } from "./DataPrivacySection";
import { DidNotAttendButton } from "./DidNotAttendButton";

export const questionnaireKey = "clinicalNotes";

export interface ClinicalNotesQuestionsProps {
    /**
     * @deprecated Use `useQuestionnaireContainer` or `useQuestionnaireContainerForSection` instead
     */
    readonly?: boolean;
    questions?: AnsweredQuestionWithId[];
    employeeRecord?: EmployeeRecord;
    ohCase?: PartialOhCaseDetailModel;
}

export interface ClinicalNotesProps extends ClinicalNotesQuestionsProps {
    /**
     * @deprecated Use `useQuestionnaireContainer` or `useQuestionnaireContainerForSection` instead
     */
    questionnaireKey?: string;
    index?: number;
};

interface ClinicalNotesStateProps {
    customerId: string;
    taskId: string;
    task: TaskModel | undefined;
    ohCaseId: string;
    currentUser: CurrentUser;
    ohCase: OhCaseListModel | (OhCaseDetailModel & { detailUpdated: moment.Moment });
    employeeRecord: EmployeeRecord | undefined;
    questions: AnsweredQuestionWithId[];
    eTag: string | null;
    readonly: boolean;
    isLoading: boolean;
    adminUsers: Partial<AdminUserDetailModel>[];
    clinicansQualification: string | undefined;
}

export interface ClinicalNotesDispatchProps {
    loadEmployee: (employeeRecordId: string, customerId: string) => void;
    startNotes: () => void;
    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion }) => void;
    // Should make AbortController mandatory as soon as practical
    loadQuestionnaire: (abortController: AbortController | undefined, ohCaseId: string, taskId: string, customerId: string) => Promise<void>;
    submitDidNotAttend: (ohCaseId: string, taskId: string, comment: string | undefined) => void;
    onCompletion: () => void;
    submitInvalidQuestionnaire: () => void;
    loadAdminUsers: () => void;
}

interface ClinicalNotesOwnProps {
    intro?: string;
    submitQuestionnaire: (ohCaseId: string, taskId: string, customerId: string, questions: AnsweredQuestionWithId[]) => void;
    // Allow to return void to maintain backwards compatibility, we should change this to Promise<void> when practical
    saveQuestionnaire: (
        ohCaseId: string, taskId: string, customerId: string, questions: AnsweredQuestionWithId[], eTag: string | null
    ) => (void | Promise<any>);
    children?: React.ReactNode;
    hideDidNotAttendButton?: boolean;
}

type AllProps = ClinicalNotesStateProps & ClinicalNotesDispatchProps & ClinicalNotesOwnProps;

interface State {
    dnaModalOpen: boolean;
    dnaComment: string | undefined;
    saving: boolean;
    submitted: boolean;
    openConfirmation: boolean;
    appointment: Appointment | undefined;
    loadedAppointment: boolean;
}

class ClinicalNotesUnconnected extends React.Component<AllProps, State> {
    public readonly consultationDetailsSectionId = "ConsultationDetails";
    public readonly clinicianDetailsSectionId = "ClinicianDetails";
    public readonly startTimeQuestion = { questionText: "The start time of the consultation", id: "StartTime" };
    public readonly finishTimeQuestion = { questionText: "The finish time of the consultation", id: "FinishTime" };
    public readonly clinicianIdQuestion = { questionText: "", id: "CliniciansId" };
    public readonly clinicianNameQuestion = { questionText: "Clinician's name", id: "CliniciansName" };
    public readonly clinicianQualificationQuestion = { questionText: "Clinician's qualification", id: "CliniciansQualification" };
    public readonly completedOnBehalfOfQuestion = { questionText: "Completed on behalf of", id: "CompletedOnBehalfOf" };
    public readonly completedOnBehalfOfIdQuestion = { questionText: "", id: "CompletedOnBehalfOfId" };
    public readonly completedOnBehalfOfNameQuestion = { questionText: "", id: "CompletedOnBehalfOfName" };
    public readonly completedOnBehalfOfQualificationQuestion = { questionText: "", id: "CompletedOnBehalfOfQualification" };
    private readonly immunisationWorkflowImprovementsPt1Enabled = getFeatureToggles()[FeatureToggleNames.ImmunisationWorkflowImprovementsPt1];

    private async saveQuestionnaire(questions: AnsweredQuestionWithId[], eTag: string | null) {
        // saveQuestionnaire might return either void or Promise<void> depending on whether it is async or not
        // eslint-disable-next-line no-return-await
        return await this.props.saveQuestionnaire(this.props.ohCaseId, this.props.taskId, this.props.customerId, questions, eTag);
    }

    constructor(props: AllProps) {
        super(props);

        this.state =  {
            saving: false,
            submitted: false,
            dnaModalOpen: false,
            dnaComment: undefined,
            openConfirmation: false,
            appointment: undefined,
            loadedAppointment: false
        };

        this.saveQuestionnaire = this.saveQuestionnaire.bind(this);
        this.removeAppointment = this.removeAppointment.bind(this);
        this.submitQuestionnaire = this.submitQuestionnaire.bind(this);
        this.loadQuestionnaire = this.loadQuestionnaire.bind(this);
        this.updateCompletedOnBehalfOfAnswers = this.updateCompletedOnBehalfOfAnswers.bind(this);
        if (this.props.ohCase) {
            this.props.loadEmployee(this.props.ohCase.employeeRecordId, this.props.customerId);
        }
    }

    public async loadQuestionnaire(abortController?: AbortController) {
        await this.props.loadQuestionnaire(abortController, this.props.ohCaseId, this.props.taskId, this.props.customerId);

        if (!this.props.readonly) {
            this.props.updateAnswer({
                sectionId: this.consultationDetailsSectionId,
                id: this.startTimeQuestion.id,
                question: { question: this.startTimeQuestion.questionText, answer: moment.utc().format("YYYY-MM-DD HH:mm:ss"), isValid: true }
            });
        }
        if (this.props.currentUser) {
            this.updateCurrentUserAnswers();
        }
    }

    public componentDidUpdate?(prevProps: ClinicalNotesStateProps) {
        if (this.props.currentUser &&
            (this.props.currentUser !== prevProps.currentUser || this.props.clinicansQualification !== prevProps.clinicansQualification)) {
            this.updateCurrentUserAnswers();
        }
        if (this.props.ohCase && this.props.ohCase !== prevProps.ohCase) {
            this.props.loadEmployee(this.props.ohCase.employeeRecordId, this.props.customerId);
        }
        if (this.props.questions.find(q => q.id === this.completedOnBehalfOfQuestion.id)?.answer
            !== prevProps.questions.find(q => q.id === this.completedOnBehalfOfQuestion.id)?.answer) {
            this.updateCompletedOnBehalfOfAnswers();
        }
        if (this.props.task && this.props.task.appointmentId && !this.state.appointment && !this.state.loadedAppointment) {
            this.setState({
                loadedAppointment: true
            });

            calendarApi.get(this.props.task.appointmentId).then(response => { // eslint-disable-line promise/always-return
                this.setState({
                    appointment: response
                });
            });
        }
        if (!this.props.readonly && prevProps.readonly && this.props.adminUsers.length === 0) {
            this.props.loadAdminUsers();
        }
    }

    public updateCurrentUserAnswers() {
        if (!this.props.readonly) {
            this.props.updateAnswer({
                sectionId: this.consultationDetailsSectionId,
                id: this.clinicianIdQuestion.id,
                question: { question: this.clinicianIdQuestion.questionText, answer: this.props.currentUser.id, isValid: true }
            });
            this.props.updateAnswer({
                sectionId: this.consultationDetailsSectionId,
                id: this.clinicianNameQuestion.id,
                question: { question: this.clinicianNameQuestion.questionText, answer: this.props.currentUser.name, isValid: true }
            });
            this.props.updateAnswer({
                sectionId: this.consultationDetailsSectionId,
                id: this.clinicianQualificationQuestion.id,
                question: { question: this.clinicianQualificationQuestion.questionText, answer: this.props.clinicansQualification, isValid: true }
            });
        }
    }

    public updateCompletedOnBehalfOfAnswers() {
        if (!this.props.readonly) {
            const adminUserId = getAnswerToQuestionWithId(this.props.questions, this.completedOnBehalfOfQuestion.id);
            const adminUser = this.props.adminUsers.find(u => u.id === adminUserId);
            const completedOnBehalfOfId = adminUserId ?? "";
            const completedOnBehalfOfFullName = adminUser ? getUserDisplayName(adminUser, true, true) : "";
            const completedOnBehalfOfQualification = adminUser?.qualification ?? "";
            this.props.updateAnswer({
                sectionId: this.consultationDetailsSectionId,
                id: this.completedOnBehalfOfIdQuestion.id,
                question: { question: this.completedOnBehalfOfIdQuestion.questionText, answer: completedOnBehalfOfId, isValid: true }
            });
            this.props.updateAnswer({
                sectionId: this.consultationDetailsSectionId,
                id: this.completedOnBehalfOfNameQuestion.id,
                question: { question: this.completedOnBehalfOfNameQuestion.questionText, answer: completedOnBehalfOfFullName, isValid: true }
            });
            this.props.updateAnswer({
                sectionId: this.consultationDetailsSectionId,
                id: this.completedOnBehalfOfQualificationQuestion.id,
                question: { question: this.completedOnBehalfOfQualificationQuestion.questionText, answer: completedOnBehalfOfQualification, isValid: true }
            });
        }
    }

    public removeAppointment() {
        return new TasksApi().removeAppointment(this.props.task.caseId, this.props.task.id);
    }

    public async submitQuestionnaire(questions: AnsweredQuestionWithId[]) {
        if (!questions.every(question => question.isValid)) {
            this.props.submitInvalidQuestionnaire();
            return false;
        }

        const finishTimeAnswerIndex = questions.findIndex(q => q.sectionId === this.consultationDetailsSectionId && q.id === this.finishTimeQuestion.id);
        const finishTimeAnswer = {
            id: this.finishTimeQuestion.id,
            question: this.finishTimeQuestion.questionText,
            answer: moment.utc().format("YYYY-MM-DD HH:mm:ss"),
            isValid: true,
            sectionId: this.consultationDetailsSectionId
        };
        if (finishTimeAnswerIndex > -1) {
            questions[finishTimeAnswerIndex] = finishTimeAnswer;
        } else {
            questions.push(finishTimeAnswer);
        }

        this.setState({ submitted: true });
        if (this.props.saveQuestionnaire) {
            await this.props.saveQuestionnaire(this.props.ohCaseId, this.props.taskId, this.props.customerId, questions, this.props.eTag);
        }
        await this.props.submitQuestionnaire(this.props.ohCaseId, this.props.taskId, this.props.customerId, questions);

        return true;
    }

    public render() {
        if (this.props.isLoading || !("detailUpdated" in this.props.ohCase)) {
            return <Loader active />;
        }

        const questionnaireContainerContent = (<>
            {
                this.props.readonly && <AccordionQuestionnaireSection title="Consultation details" id={this.consultationDetailsSectionId}>
                    <OldDateQuestion readonly={true} questionConfig={this.startTimeQuestion} />
                    <OldDateQuestion readonly={true} questionConfig={this.finishTimeQuestion} />
                    <TextQuestion readonly={true} questionConfig={this.clinicianNameQuestion} />
                </AccordionQuestionnaireSection>}
            <DataPrivacySection />
            <AccordionQuestionnaireSection
                title="Confidentiality"
                id="Confidentiality"
            >
                <p>For confidentiality reasons before we proceed I need to just ask you a few security questions?</p>
                <CheckboxQuestion
                    readonly={this.props.readonly}
                    questionConfig={({
                        id: "ConfirmDOB",
                        // eslint-disable-next-line max-len
                        questionText: `Please confirm your DOB? (${this.props.ohCase && this.props.ohCase.dateOfBirth && this.props.ohCase.dateOfBirth.format("L")})`
                    })}
                    value="Confirmed"
                />
                <CheckboxQuestion
                    readonly={this.props.readonly}
                    questionConfig={({
                        id: "ConfirmAddress",
                        // eslint-disable-next-line max-len
                        questionText: `Please could you also confirm the first line of your address and postcode? ${this.employeeAddress ? `(${this.employeeAddress})` : ""}`
                    })}
                    value="Confirmed"
                />
                <CheckboxQuestion
                    readonly={this.props.readonly}
                    questionConfig={({
                        id: "ConfirmOtherIdentifier",
                        questionText: "If you are unable to match the DOB with address/postcode, use another identifier"
                    })}
                    value="Confirmed"
                />
            </AccordionQuestionnaireSection>
            <AccordionQuestionnaireSection
                title="Clinician Details"
                id={this.clinicianDetailsSectionId}
            >
                <DropdownQuestion
                    readonly={this.props.readonly}
                    questionConfig={this.completedOnBehalfOfQuestion}
                    options={this.props.adminUsers.map(u => ({ text: getUserDisplayName(u), value: u.id }))}
                    search
                    selectOnBlur={false}
                />
            </AccordionQuestionnaireSection>
            <AccordionQuestionnaireSection
                title="Data Protection"
                id="DataProtection"
            >
                <p>In line with the UK Data Protection Act, General Data Protection Regulations
                I am also required to confirm the following with you.</p>
                <CheckboxQuestion
                    readonly={this.props.readonly}
                    questionConfig={({
                        id: "ConsentStoreData",
                        // eslint-disable-next-line max-len
                        questionText: "Do you understand that by participating in this consultation you will be providing Optima Health with consent to store and process any personal and health related information that is discussed during the call?"
                    })}
                    value="Consented"
                />
                { this.immunisationWorkflowImprovementsPt1Enabled &&
                    <ContextAwareCheckboxQuestion
                        preserveInitialQuestionText
                        questionConfig={({
                            id: "RequestDataExplained",
                            // eslint-disable-next-line max-len
                            questionText: "Do you understand that you can request a copy of your clinical notes at any time? Our registered address can be found on our website, www.optimahealth.co.uk"
                        })}
                        value="Explained"
                    />
                }
                { !this.immunisationWorkflowImprovementsPt1Enabled &&

                    <CheckboxQuestion
                        readonly={this.props.readonly}
                        questionConfig={({
                            id: "RequestDataExplained",
                            // eslint-disable-next-line max-len
                            questionText: "You can request a copy of your clinical notes at any time by writing to us at our registered address which can be found on our website <a href=\"https://tphealth.co.uk\" target=\"_blank\" rel=\"noopener noreferrer\">https://tphealth.co.uk</a>"
                        })}
                        value="Explained"
                    />
                }
                { this.immunisationWorkflowImprovementsPt1Enabled &&
                    <ContextAwareCheckboxQuestion
                        preserveInitialQuestionText
                        questionConfig={({
                            id: "ConfirmPrivacyStatementExplained",
                            // eslint-disable-next-line max-len
                            questionText: "Please note that you can access our full privacy policy on our website, which clearly explains how, where, and when your personal and health data will be processed in detail."
                        })}
                        value="Explained"
                    />
                }
                { !this.immunisationWorkflowImprovementsPt1Enabled &&
                    <CheckboxQuestion
                        readonly={this.props.readonly}
                        questionConfig={({
                            id: "ConfirmPrivacyStatementExplained",
                            // eslint-disable-next-line max-len
                            questionText: "Please note that you can access our full <a href=\"https://tphealth.co.uk/privacy-statement\" target=\"_blank\" rel=\"noopener noreferrer\">Privacy Statement</a> on our website which clearly explains in detail how, where and when your personal and health data will be processed."
                        })}
                        value="Explained"
                    />
                }
            </AccordionQuestionnaireSection>
            {React.Children.map(this.props.children, (c: React.ReactElement<ClinicalNotesQuestionsProps>) => React.cloneElement(c, {
                ...c.props,
                employeeRecord: this.props.employeeRecord,
                ohCase: this.props.ohCase,
                questions: this.props.questions,
                readonly: this.props.readonly
            }))}
        </>);
        return (
            <Container className="clinical-notes">
                <h2 className={this.props.readonly ? "" : "sticky-header"}>
                    Clinical Notes for {this.props.ohCase && `${this.props.ohCase.employeeFirstName} ${this.props.ohCase.employeeLastName}`}
                </h2>
                {this.props.readonly ?
                    <CompletedDate readonly={this.props.readonly} task={this.props.task} />
                    :
                    <>
                        <p>{this.props.intro}</p>
                        <Container textAlign="right" className="start-button">
                            <a
                                href={`/customers/${this.props.customerId}/employeeRecords/${this.props.ohCase.employeeRecordId}`
                                    + "/clinicalHistory"}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Button content="View Clinical History" />
                            </a>
                            { !this.props.hideDidNotAttendButton &&
                                <DidNotAttendButton
                                    customerId={this.props.customerId}
                                    ohCaseId={this.props.ohCaseId}
                                    task={this.props.task}
                                />
                            }
                            <Button onClick={this.props.startNotes} content="Start Notes" />
                        </Container>
                    </>}
                <AccordionQuestionnaireContainer
                    autoSaveQuestionnaire={!this.props.readonly && !this.state.submitted ? this.saveQuestionnaire : undefined}
                    loadQuestionnaire={this.loadQuestionnaire as ((abortController?: AbortController) => Promise<void>)}
                    submitQuestionnaire={this.submitQuestionnaire}
                    readonly={this.props.readonly}
                    questionnaireKey={questionnaireKey}
                    questionnaireType={QuestionnaireTypes.ClinicalNotes}
                    task={this.props.task}
                    removeAppointment={this.removeAppointment}
                >
                    {questionnaireContainerContent}
                </AccordionQuestionnaireContainer>
            </Container>
        );
    }

    public get employeeAddress() {
        return this.props.employeeRecord ?
            [
                this.props.employeeRecord.addressLine1,
                this.props.employeeRecord.postCode
            ].filter(x => x).join(" ") :
            null;
    }
}

const mapStateToProps = (
    state: ApplicationState & EmployeeRecordState & ActionableTasksState
): ClinicalNotesStateProps => {
    const taskId = state.router.params && state.router.params.taskId!;
    let task = state.actionableTasks.tasks.find(t => t.id === taskId);
    const ohCaseId = ohCaseIdSelector(state) || (task && task.caseId);
    const ohCase = state.ohCases.find(c => c.id === ohCaseId) as OhCaseListModel | OhCaseDetailModel;
    let employeeRecord;
    let isLoading = true;
    if (ohCase && "tasks" in ohCase && ohCase.tasks) {
        task = task || ohCase.tasks.find(t => t.id === taskId);
        employeeRecord = state.employeeRecordsDetail.employeeRecords.find(e => e.id === ohCase.employeeRecordId) as EmployeeRecord;
        if (state.currentUser) {
            isLoading = false;
        }
    }
    const adminUsers = adminUsersSelector(state);
    return {
        customerId: state.router.params?.customerId || task?.customer.id,
        ohCaseId,
        taskId,
        task,
        currentUser: state.currentUser,
        ohCase,
        questions: state.questionnaires[questionnaireKey] ?
            uniq(flatten(state.questionnaires[questionnaireKey].sections.map(section => values(section.questions)))) :
            [],
        eTag: state.questionnaires[questionnaireKey]?.eTag ?? null,
        readonly: (!task || task?.completed),
        isLoading,
        employeeRecord,
        adminUsers,
        clinicansQualification: adminUsers.find(u => u.id === state.currentUser.id)?.qualification
    };
};

const mapDispatchToProps = (dispatch: any): ClinicalNotesDispatchProps => {
    return {
        startNotes: () => dispatch(openSection({ sectionId: "DataPrivacy", questionnaireKey })),
        loadQuestionnaire: (abortController: AbortController, ohCaseId: string, taskId: string) =>
            dispatch(loadQuestionnaire(abortController, ohCaseId, taskId, questionnaireKey)),
        updateAnswer: payload => dispatch(updateQuestion({ ...payload, questionnaireKey })),
        submitDidNotAttend: (ohCaseId: string, taskId: string, comment: string) =>
            dispatch(submitDidNotAttend(ohCaseId, taskId, comment)),
        loadEmployee: (employeeRecordId: string, customerId: string) => dispatch(loadEmployeeRecordDetail({ employeeRecordId, customerId })),
        onCompletion: () => dispatch(navigateToParentRoute()),
        submitInvalidQuestionnaire: () => dispatch(submitInvalidQuestionnaire(questionnaireKey)),
        loadAdminUsers: () => dispatch(loadAdminUsers())
    };
};

const mergeProps = (stateProps: ClinicalNotesStateProps, dispatchProps: ClinicalNotesDispatchProps, ownProps: ClinicalNotesOwnProps): AllProps => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps
});

export const ClinicalNotes = connect(mapStateToProps, mapDispatchToProps, mergeProps)(ClinicalNotesUnconnected);
